import { useCallback, useEffect, useRef } from "react"
import { EXPLORE_TYPE_ALL, EXPLORE_TYPES } from "@/constants/explore"
import {
  SAVED_TAB_DEFAULT,
  SAVED_TAB_VALUES,
  SAVED_TYPE_DEFAULT,
  SAVED_TYPE_VALUES,
} from "@/constants/saved"
import { isAgentRunRef } from "@/types/agent-summary"
import { Entity, EventActorRef, InspirationRef, PlaceRef } from "@/types/entity"
import { ExploreLocation, ExploreType } from "@/types/explore"
import { FlightSearchInput } from "@/types/flights/flight-search-input"
import { ItineraryEvent } from "@/types/itinerary"
import { SavedTab, SavedType } from "@/types/saved"
import {
  SettingEarlyAccessTab,
  SettingTabType,
  SettingTabTypeValues,
} from "@/types/settings"
import { isTripRef } from "@/types/trip"
import { CurrentUser } from "@/types/user"
import isEqual from "lodash/isEqual"
import NProgress from "nprogress"

import {
  eventActorToAmplitudeProperties,
  inspirationToAmplitudeProperties,
  placeToAmplitudeProperties,
} from "@/lib/analytics/amplitude-properties"
import {
  clearAnalyticsChatProperties,
  sendAnalyticsEvent,
} from "@/lib/analytics/amplitude-utils"
import { addEmbeddedParams, getAuthTokenFromUrl } from "@/lib/auth-utils"
import {
  applyLocatorToQuery,
  buildHistoryStateWithDetailSheetDepth,
  detailSheetQueryHistoryOp,
  entryMatchesLocator,
  getDetailSheetHistoryDepth,
  getPopstateDetailSheetHistoryIndex,
  locatorForEntry,
  locatorFromQuery,
  locatorsEqual,
  sheetKindForRef,
} from "@/lib/detail-sheet-url"
import {
  entityIsEventActor,
  entityIsGuide,
  entityIsPlace,
} from "@/lib/entity-utils"
import { httpIsCancelled } from "@/lib/exception-utils"
import {
  buildFlightSearchInputFromParams,
  buildFlightSheetUrlParams,
} from "@/lib/flight-utils"
import { DefaultInspirationItemFetchSize } from "@/lib/inspiration"
import { isClientSide } from "@/lib/is-client-side"
import { isEmbedded } from "@/lib/is-embedded"
import { mtLocation } from "@/lib/mt-location"
import { startRouteChangeTiming } from "@/lib/perf/route-change-timing"
import { placeDetailCTA } from "@/lib/place-utils"
import { useRouter } from "@/lib/router"
import { eventActorApi } from "@/lib/storage/api/event-actor-api"
import { inspirationApi } from "@/lib/storage/api/inspiration-api"
import { placeApi } from "@/lib/storage/api/place-api"
import {
  selectBookDirectOnly,
  selectEmbedded,
  selectIsXS,
} from "@/lib/storage/app-slice"
import { runPendingChatKickoff } from "@/lib/storage/chat-thunks"
import { selectCurrentPage as selectCollectionCurrentPage } from "@/lib/storage/collection-slice"
import creatorSlice, {
  AdminTabDraftsType,
  AdminTabReportType,
  AdminTabType,
  AdminTabTypeList,
  CreateTabGuidesType,
  CreateTabType,
  CreateTabTypeList,
  selectAdminTab,
  selectCreateTab,
} from "@/lib/storage/creator-slice"
import {
  closeDetailSheet,
  navigateToHistoryIndexThunk,
  openActionPillAnswerSheet,
  openAgentRunReportSheet,
  openEntitySheet,
  openWebEventSheetByRef,
  selectDetailSheetCurrentPage,
  selectDetailSheetHistory,
  selectDetailSheetOpen,
  selectDetailSheetUrlLocator,
  selectEntitySheetEntity,
  selectEntitySheetEntityRef,
  selectEntitySheetItineraryEvent,
  selectEntitySheetPriceQuoteRef,
  selectEntitySheetTrackingContext,
  selectTripInSheetStack,
  selectTripPageActive,
} from "@/lib/storage/detail-sheet-slice"
import exploreSlice, {
  selectIsLocationUserSelected as selectExploreIsLocationUserSelected,
  selectLocation as selectExploreLocation,
  selectSearchString as selectExploreSearchString,
  selectSelectedType as selectExploreSelectedType,
} from "@/lib/storage/explore-slice"
import flightSearchSlice, {
  closeFlightSheet,
  openFlightSheetWithSearchInput,
  selectSheetSearchInput,
} from "@/lib/storage/flight-search-slice"
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/lib/storage/hooks"
import inspirationSlice, {
  selectCurrentPage as selectInspirationCurrentPage,
  selectMaxPage as selectInspirationMaxPage,
  selectSearchTerm as selectInspirationSearchTerm,
} from "@/lib/storage/inspiration-slice"
import itinerarySlice from "@/lib/storage/itinerary-slice"
import {
  setHoveredFlight,
  setHoveredPlace,
} from "@/lib/storage/map-hover-slice"
import { setPreviousPage } from "@/lib/storage/navigation-slice"
import { showPostSignupPromptsIfNecessary } from "@/lib/storage/post-signup-thunks"
import profileSlice, {
  ProfileTabDefault,
  ProfileTabType,
  ProfileTabTypeValues,
  selectUserProfileTab,
  selectUserSettingTab,
} from "@/lib/storage/profile-slice"
import { changeSettingsTab } from "@/lib/storage/profile-thunks"
import publicProfileSlice, {
  PublicProfileDefaultTab,
  PublicProfileTabType,
  PublicProfileTabTypeValues,
  selectPublicTab,
} from "@/lib/storage/public-profile-slice"
import savedSlice, {
  selectCurrentTab as selectSavedCurrentTab,
  selectSelectedType as selectSavedSelectedType,
} from "@/lib/storage/saved-slice"
import { runPendingSaveAction } from "@/lib/storage/saved-thunks"
import tripSlice, { selectCurrentTab } from "@/lib/storage/trip-slice"
import { openTripSheet } from "@/lib/storage/trip-thunks"
import userSlice, {
  selectCanAdminInspiration,
  selectCanAdminVanityLinkPermission,
  selectCanViewAllVanityReportsPermission,
  selectChatId,
  selectEmailVerifyMode,
  selectIsUserCrawler,
  selectProfileSetupDialogOpen,
  selectShowCookiesBanner,
  selectUser,
} from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import {
  isChatPageUrl,
  isExplorePageUrl,
  isTripPageUrl,
  tripRefFromUrl,
} from "@/lib/utils/page"
import {
  getPageEventActorParams,
  getPageInspirationParams,
  getPageIsTripSheetOpenParams,
  getPageIsVerifyEmailOpenParams,
  getPagePlaceParams,
  isAdminPageUrl,
  isCollectionPageUrl,
  isCreatePageUrl,
  isEventActorPageUrl,
  isFlightBookingPageUrl,
  isFlightSearchPageUrl,
  isInspirationPageUrl,
  isOwnProfilePageUrl,
  isProfilePageUrl,
  isSavedPageUrl,
  isSettingPageUrl,
  isTripTab,
} from "@/lib/utils/routing"
import { singleParam } from "@/lib/utils/url"
import { copyUtmParamsInto } from "@/lib/utils/url-params"
import { webStorage } from "@/lib/web-storage"

interface RouteAffectingState {
  chatId: number
  flightSearchInput: FlightSearchInput | undefined
  detailSheetLocator: ReturnType<typeof selectDetailSheetUrlLocator>
  entitySheetEntity: Entity | undefined
  entitySheetItineraryEvent: ItineraryEvent | undefined
  entitySheetPriceQuoteRef: string | undefined
  tripInSheetStack: boolean
  emailVerifyMode: boolean | undefined
  // Reevaluate queued verification when the setup workspace closes.
  profileSetupDialogOpen: boolean
  selectedSavedTab: SavedTab
  selectedSavedType: SavedType
  selectedCreateTab: CreateTabType
  selectedInspirationSearchTerm: string | undefined
  inspirationCurrentPage: number
  selectedExploreType: ExploreType
  selectedExploreLocation: ExploreLocation | undefined
  selectedExploreSearchString: string | undefined
  selectedAdminTab: AdminTabType
  selectedProfileTab: ProfileTabType
  selectedPublicProfileTab: PublicProfileTabType
  selectedSettingTab: SettingTabType | null
  collectionCurrentPage: number
}

/*
 * This is a centralized place for syncing client side redux state and the url
 *
 * There are a few scenarios covered here:
 *   1. Initial page load, when we load the page for the server there may be some state in the url we need to sync with
 *     redux. This is handled when init.current is false and we call handleRouteChangeComplete which is the same function
 *     that is called on route change. These will happen on page refresh or when a user navigates to the page directly.
 *   2. We handle route change events, these are next js route events that don't go back to the server but are client
 *      only. Things like back/forward or when the user clicks on a link. We need to update the redux state to reflect.
 *   3. We handle redux state changes that affect the route. This is when the user changes the state of the app and we
 *      need to update the url to reflect that.
 *
 *  Note:
 *     Ideally I think we should avoid using async calls in the route handlers, This causes a lot of complexity and
 *     need for checks that the route change is still in progress. I think we should try to avoid having async calls
 *     here. However, the setEntitySheet is used in many places and I don't want to refactor that right now. If you do
 *     choose to make async calls here, make sure to make that call cancellable and cancel it when the route changes.
 */
export function ClientRouteHandler({ initUser }: { initUser?: CurrentUser }) {
  const router = useRouter()
  // Read URL directly from mtLocation on client to avoid depending on router.asPath
  // which would cause unnecessary re-renders when Next.js router state changes
  const getCurrentUrl = useCallback(
    () =>
      typeof window !== "undefined"
        ? `${mtLocation.pathname}${mtLocation.search}`
        : router.asPath,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- router.asPath only used for SSR fallback
    []
  )
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const prevRouteAffectingState = useRef<RouteAffectingState | null>(null)
  const handleRouteChangeCompleteRef = useRef<
    ((url: string) => Promise<void>) | null
  >(null)

  const flightSearchInput = useAppSelector(selectSheetSearchInput)
  const currUser = useAppSelector(selectUser)
  const chatId = useAppSelector(selectChatId)
  const entitySheetOpen = useAppSelector(selectDetailSheetOpen)
  const entitySheetEntity = useAppSelector(selectEntitySheetEntity)
  const entitySheetEntityRef = useAppSelector(selectEntitySheetEntityRef)
  const entitySheetTrackingContext = useAppSelector(
    selectEntitySheetTrackingContext
  )
  const entitySheetItineraryEvent = useAppSelector(
    selectEntitySheetItineraryEvent
  )
  const entitySheetPriceQuoteRef = useAppSelector(
    selectEntitySheetPriceQuoteRef
  )
  const detailSheetLocator = useAppSelector(selectDetailSheetUrlLocator)
  const detailSheetCurrentPage = useAppSelector(selectDetailSheetCurrentPage)
  const entitySheetHistory = useAppSelector(selectDetailSheetHistory)
  const entitySheetOpenRef = useRef(entitySheetOpen)
  const entitySheetHistoryRef = useRef(entitySheetHistory)
  const tripPageActive = useAppSelector(selectTripPageActive)
  const tripInSheetStack = useAppSelector(selectTripInSheetStack)
  const emailVerifyMode = useAppSelector(selectEmailVerifyMode)
  const profileSetupDialogOpen = useAppSelector(selectProfileSetupDialogOpen)
  const showCookiesBanner = useAppSelector(selectShowCookiesBanner)
  const routeChangeInProgress = useRef<string | null>(null)
  const prevUrl = useRef<string | null>(null)
  /** After closing the sheet with stacked entity URLs, popstate replaces to this URL */
  const entitySheetCollapseRef = useRef<{ targetUrl: string } | null>(null)
  const prevEmailValidatedRef = useRef(currUser.email_validated)
  const selectedSavedTab = useAppSelector(selectSavedCurrentTab)
  const selectedSavedType = useAppSelector(selectSavedSelectedType)
  const selectedInspirationSearchTerm = useAppSelector(
    selectInspirationSearchTerm
  )
  const inspirationCurrentPage = useAppSelector(selectInspirationCurrentPage)
  const inspirationMaxPage = useAppSelector(selectInspirationMaxPage)
  const selectedExploreType = useAppSelector(selectExploreSelectedType)
  const selectedExploreSearchString = useAppSelector(selectExploreSearchString)
  const selectedExploreLocation = useAppSelector(selectExploreLocation)
  const selectedExploreIsLocationUserSelected = useAppSelector(
    selectExploreIsLocationUserSelected
  )
  const selectedCreateTab = useAppSelector(selectCreateTab)
  const selectedProfileTab = useAppSelector(selectUserProfileTab)
  const selectedPublicProfileTab = useAppSelector(selectPublicTab)
  const selectedSettingTab = useAppSelector(selectUserSettingTab)
  const selectedAdminTab = useAppSelector(selectAdminTab)
  const canAdminInspiration = useAppSelector(selectCanAdminInspiration)
  const canAdminVanityLink = useAppSelector(selectCanAdminVanityLinkPermission)
  const canViewAllVanityReport = useAppSelector(
    selectCanViewAllVanityReportsPermission
  )
  const collectionCurrentPage = useAppSelector(selectCollectionCurrentPage)
  const isCrawler = useAppSelector(selectIsUserCrawler)
  const selectedTripTab = useAppSelector(selectCurrentTab)

  const bookDirectOnly = useAppSelector(selectBookDirectOnly)
  const embedded = useAppSelector(selectEmbedded)
  const isXS = useAppSelector(selectIsXS)

  // these are for handling route transitions state, mostly for animations and things like that, for the most part you
  // will want to use the next useffect to handle route changes
  useEffect(() => {
    const routeChangeStartListener = (
      _url: string,
      { shallow }: { shallow: boolean }
    ) => {
      if (isEmbedded() && !getAuthTokenFromUrl(_url)) {
        // carry mindtrip.auth query param forward with any router path updates
        const authorizedUrl = addEmbeddedParams(_url, router.query)
        if (_url !== authorizedUrl) {
          return void router.replace(authorizedUrl)
        }
      }

      if (!shallow) {
        NProgress.start()
      }
    }

    const routeChangeCompleteListener = (_url: string) => {
      if (NProgress.isStarted()) {
        NProgress.done()
      }
    }

    const routeChangeErrorListener = () => {
      if (NProgress.isStarted()) {
        NProgress.done()
      }
    }

    router.events.on("routeChangeStart", routeChangeStartListener)
    router.events.on("routeChangeComplete", routeChangeCompleteListener)
    router.events.on("routeChangeError", routeChangeErrorListener)

    return () => {
      router.events.off("routeChangeStart", routeChangeStartListener)
      router.events.off("routeChangeComplete", routeChangeCompleteListener)
      router.events.off("routeChangeError", routeChangeErrorListener)
    }
  }, [router, dispatch])

  useEffect(() => {
    startRouteChangeTiming()
  }, [])

  // handle a pending save, and a kickoff the sign-up gate kept, after email
  // verification completes
  // this is needed because emailVerifyMode is cleared before the verify dialog shows,
  // so there's no routeAffectingState change when verification completes
  useEffect(() => {
    const wasValidated = prevEmailValidatedRef.current
    const isNowValidated = currUser.email_validated
    prevEmailValidatedRef.current = isNowValidated

    // only trigger on the transition from unvalidated to validated
    if (!wasValidated && isNowValidated) {
      void dispatch(runPendingSaveAction())
      runPendingChatKickoff(store.dispatch, store.getState)
    }
  }, [currUser.email_validated, dispatch, store])

  // handle redux state changes that affect the url
  function handleReduxStateChange(
    currentRouteAffectingState: RouteAffectingState
  ) {
    const prev = prevRouteAffectingState.current
    prevRouteAffectingState.current = currentRouteAffectingState

    const current = getCurrentUrl().split("?")
    const currentRoutePath = current[0]
    const currentRouteQuery = new URLSearchParams(current[1] ?? "")

    void dispatch(showPostSignupPromptsIfNecessary())

    // routes for which we want to expose URLs in the browser from redux state changes
    //
    //  /flightSearch
    //
    //  /chat
    //  /chat/1234
    //  /chat/1234?ref=lo-9x8YmTHX -> with place/guide/event actor slideout
    //  /chat/1234?ref=we-7aLZJ9Hw -> with web event search result slideout
    //  /chat/1234?ref=ar-Kq2mPz8L -> with agent run report slideout
    //  /home?ref=pi-7aLZJ9Hw -> with action pill answer slideout
    //  /chat/1234?ref=t-XqMGiOUD -> with trip page slideout
    //
    //  /profile/emilytraveling
    //  /profile/emilytraveling?ref=re-bUwR1eG5 -> with place/guide/event actor slideout
    //  /profile/emilytraveling?tab=trips|places|guides
    //
    //  /saved
    //  /saved?ref=lo-9x8YmTHX -> when in All type
    //  /saved?type=restaurant
    //  /saved?ref=re-5gPq2B0O&type=restaurant -> when in restaurant type and slide out
    //  /saved?tab=guides
    //
    //  /settings
    //  /settings?section=account|personalization|payment|early-access -> sections
    //  /settings?ea=<hash> -> early access opt-in
    //
    //  /create
    //  /create?tab=links|guides|earnings

    //  /explore
    //  /explore?type=all|hotel|restaurant|attraction|guide|people

    //  *** /inspiration and /admin had a slightly different pattern: ***
    //
    //  /inspiration
    //  /inspiration?tab=all|itineraries|lists|people
    //  /inspiration/san-francisco-best-restaurants/in-IpgED29x -> guide itself
    //  /inspiration/san-francisco-best-restaurants/in-IpgED29x?ref=re-bUwR1eG5 -> with place slide out
    //
    //  /admin
    //  /admin?tab=drafts|published|report|earnings|creators
    //  /admin/san-francisco-best-restaurants/in-IpgED29x -> guide itself
    //  /admin/san-francisco-best-restaurants/in-IpgED29x?ref=re-bUwR1eG5 -> with place slide out
    //
    //  /trip/t-XqMGiOUD
    //  /trip/t-XqMGiOUD?ref=t-XqMGiOUD -> with trip page slideout
    //  /trip/t-XqMGiOUD?ref=lo-9x8YmTHX -> with place slideout
    //
    // Dec 15, 2025 - make sure not to match /share/chat.  /share/chat is not /chat
    // Bare /admin is the creators page; /admin/loyalty-programs and the other admin tools are their own pages.
    const regex =
      /^(?!\/share\/)(\/((?:[a-z0-9-]+\/)?chat(\/\d+)?|(inspiration\/(.+)\/(in-[^/|^?]+))|(collection\/(.+)\/(co-[^/|^?]+))|inspiration|explore|(admin\/(.+)\/(in-[^/|^?]+))|admin(?=$|[?#])|saved|create|settings|(profile\/[a-z0-9.\-_]+)|profile|(trip\/[^/|^?]+)))/
    // Use mtLocation (via getCurrentUrl) so route handling stays aligned with the
    // browser history entry during transitions; router.asPath can lag on back/forward.
    const currentUrlForRoute = getCurrentUrl()
    const regRes = regex.exec(currentUrlForRoute)
    const isKnownRoute = !!regRes?.[1]

    const currEntity = currentRouteAffectingState.entitySheetEntity
    const currEntityPriceQuoteRef =
      currentRouteAffectingState.entitySheetPriceQuoteRef
    let replaceRoute = false
    const shallow = true

    let newRoutePath: string
    let newRouteQuery: URLSearchParams

    const addSheetLocatorToQuery = () => {
      applyLocatorToQuery(
        newRouteQuery,
        currentRouteAffectingState.detailSheetLocator
      )
    }

    if (!isKnownRoute) {
      if (
        locatorsEqual(
          prev?.detailSheetLocator,
          currentRouteAffectingState.detailSheetLocator
        )
      ) {
        return
      }
      newRoutePath = currentRoutePath
      newRouteQuery = new URLSearchParams(currentRouteQuery)
      addSheetLocatorToQuery()
    } else {
      newRoutePath = regRes[1]
      newRouteQuery = new URLSearchParams()

      const currFlightSearchInput = currentRouteAffectingState.flightSearchInput
      if (currFlightSearchInput) {
        // don't change path, keeps it a query-only change which uses
        // window.history.pushState instead of router.push, avoiding re-renders
        // in all components that use useRouter()
        newRouteQuery = buildFlightSheetUrlParams(currFlightSearchInput)
        newRouteQuery.set("view", "flight-search")

        // the route will get updated on every search param change, but we only want back/forth to work if options
        // change. router.replace will replace the current url in history so back/forward won't work
        replaceRoute =
          isFlightSearchPageUrl(getCurrentUrl()) &&
          isEqual(
            currentRouteQuery.get("selected_options"),
            newRouteQuery.get("selected_options")
          )
      } else if (isChatPageUrl(newRoutePath)) {
        // Get the chatId
        const routeChatId = /^\/(?:(?:[a-z0-9-]+\/)?chat)\/(\d+)/.exec(
          currentUrlForRoute
        )?.[1]
        const chatIdChanged = prev != null && prev.chatId !== chatId
        // Only sync Redux chatId into the URL when Redux drove the change (e.g.
        // goToChat / createChat before URL catch-up). When the URL already has a
        // different chat id (e.g. Link from trip hub), trust the URL and let
        // initChatPage load it — otherwise stale chatId overwrites the history stack.
        if (
          chatId &&
          routeChatId !== chatId.toString() &&
          (chatIdChanged || routeChatId === undefined)
        ) {
          const basePage =
            /^\/[a-z0-9-]+\/chat/.exec(newRoutePath)?.[0] ?? "/chat"
          newRoutePath = `${basePage}/${chatId}`

          if (routeChatId === undefined) {
            // Desktop bare /chat → createChat: replace avoids an extra history entry.
            // App home (`/home` or XS bare `/chat`): push so back returns to that home.
            const isHomeUrlPush =
              !embedded &&
              (currentRoutePath === "/home" ||
                (isXS && currentRoutePath === "/chat"))
            replaceRoute = !isHomeUrlPush
          }
        }
        addSheetLocatorToQuery()
        if (entityIsPlace(currEntity) && currEntityPriceQuoteRef) {
          newRouteQuery.set("quoteRef", currEntityPriceQuoteRef)
        }
      } else if (isSavedPageUrl(newRoutePath)) {
        addSheetLocatorToQuery()

        if (
          selectedSavedTab !== SAVED_TAB_DEFAULT &&
          SAVED_TAB_VALUES.includes(selectedSavedTab)
        ) {
          newRouteQuery.set("tab", selectedSavedTab)
        }

        if (
          selectedSavedType !== SAVED_TYPE_DEFAULT &&
          SAVED_TYPE_VALUES.includes(selectedSavedType)
        ) {
          newRouteQuery.set("type", selectedSavedType)
        }
      } else if (isCollectionPageUrl(newRoutePath)) {
        addSheetLocatorToQuery()

        if (collectionCurrentPage > 1) {
          newRouteQuery.set("page", collectionCurrentPage.toString())
        }
      } else if (isProfilePageUrl(newRoutePath)) {
        addSheetLocatorToQuery()
        if (
          selectedProfileTab !== ProfileTabDefault &&
          ProfileTabTypeValues.includes(selectedProfileTab as ProfileTabType) &&
          currUser &&
          isOwnProfilePageUrl(newRoutePath, currUser.handle)
        ) {
          newRouteQuery.set("tab", selectedProfileTab)
        } else if (
          selectedPublicProfileTab !== PublicProfileDefaultTab &&
          PublicProfileTabTypeValues.includes(
            selectedPublicProfileTab as PublicProfileTabType
          )
        ) {
          newRouteQuery.set("tab", selectedPublicProfileTab)
        }
      } else if (isSettingPageUrl(newRoutePath)) {
        // Settings page, we show the ?section=...
        if (selectedSettingTab) {
          newRouteQuery.set("section", selectedSettingTab)
        }
        if (router.query.code) {
          newRouteQuery.set("code", router.query.code as string)
        }
      } else if (isCreatePageUrl(newRoutePath)) {
        // Create page, we show the ?tab=...
        if (selectedCreateTab) {
          newRouteQuery.set("tab", selectedCreateTab)
        }
      } else if (isInspirationPageUrl(newRoutePath)) {
        const existingInspirationName = regex.exec(currentUrlForRoute)?.[5]
        const existingInspirationRef = regex.exec(currentUrlForRoute)?.[6]
        const basePage = "/inspiration"
        if (existingInspirationName && existingInspirationRef) {
          newRoutePath = `${basePage}/${existingInspirationName}/${existingInspirationRef}`
        }

        if (entityIsPlace(currEntity)) {
          newRouteQuery.set("ref", currEntity.entity_ref)
        } else if (entityIsGuide(currEntity)) {
          // Rewrite with the canonical name (in case the URL was changed)
          const currEntityRef = currEntity.entity_ref
          const canonicalName = currEntity.canonical_name
          newRoutePath = `${basePage}/${canonicalName}/${currEntityRef}`
        } else if (entityIsEventActor(currEntity)) {
          newRouteQuery.set("ref", currEntity.entity_ref)
        }

        // For /inspiration, we want to carry over the utm_* in the URL
        if (entityIsGuide(currEntity)) {
          // Carry over the utm_* for the initial page
          copyUtmParamsInto(router.query, newRouteQuery)
        }

        if (!currEntity) {
          if (selectedInspirationSearchTerm) {
            newRouteQuery.set("search", selectedInspirationSearchTerm)
          }
          if (isCrawler && inspirationCurrentPage > 1) {
            newRouteQuery.set("page", inspirationCurrentPage.toString())
          }
        }

        if (router.query.tm) {
          newRouteQuery.set("tm", router.query.tm as string)
        }

        if (router.query.callbackUrl) {
          newRouteQuery.set("callbackUrl", router.query.callbackUrl as string)
        }
      } else if (isExplorePageUrl(newRoutePath)) {
        addSheetLocatorToQuery()

        // For /explore, show the type=... if it's not the default type
        if (selectedExploreSearchString) {
          newRouteQuery.set("search", selectedExploreSearchString)
          if (selectedExploreType !== EXPLORE_TYPE_ALL) {
            newRouteQuery.set("type", selectedExploreType)
          }
        } else if (
          selectedExploreType &&
          selectedExploreType !== EXPLORE_TYPE_ALL
        ) {
          newRouteQuery.set("type", selectedExploreType)
        }

        if (
          selectedExploreLocation?.entity_ref &&
          selectedExploreIsLocationUserSelected
        ) {
          newRouteQuery.set("loc", selectedExploreLocation.entity_ref)
        }
      } else if (isAdminPageUrl(newRoutePath)) {
        const existingInspirationName = regex.exec(currentUrlForRoute)?.[8]
        const existingInspirationRef = regex.exec(currentUrlForRoute)?.[9]
        const basePage = "/admin"
        if (existingInspirationName && existingInspirationRef) {
          newRoutePath = `${basePage}/${existingInspirationName}/${existingInspirationRef}`
        }

        if (entityIsPlace(currEntity)) {
          newRouteQuery.set("ref", currEntity.entity_ref)
        } else if (entityIsGuide(currEntity)) {
          const currEntityRef = currEntity.entity_ref
          const canonicalName = currEntity.canonical_name
          newRoutePath = `${basePage}/${canonicalName}/${currEntityRef}`
        } else if (entityIsEventActor(currEntity)) {
          newRouteQuery.set("ref", currEntity.entity_ref)
        }

        // For /admin, show the tab=... only if
        // 1) we are not showing detail (i.e. !currEntity)
        // 2) we don't want to show the default tab=all (just leave it blank)
        if (
          !currEntity &&
          selectedAdminTab &&
          selectedAdminTab !== AdminTabDraftsType
        ) {
          newRouteQuery.set("tab", selectedAdminTab)
        }
      } else if (isTripPageUrl(newRoutePath)) {
        addSheetLocatorToQuery()
      }

      // Rewrite ?tripSheet=1 and ?tripTab= deep links in place so Back doesn't
      // reopen them. The trip tab is deliberately not written back: it's view
      // state within the trip page, and pushing it would make Back step
      // through tabs.
      if (
        currentRouteQuery.has("tripSheet") ||
        currentRouteQuery.has("tripTab")
      ) {
        replaceRoute = true
      }

      if (isEmbedded()) {
        const orgid = singleParam(router.query?.["orgid"])
        if (orgid) {
          newRouteQuery.set("orgid", orgid)
        }

        const orgurl = singleParam(router.query?.["orgurl"])
        if (orgurl) {
          newRouteQuery.set("orgurl", orgurl)
        }

        const geoName = singleParam(router.query?.["geoname"])
        if (geoName) {
          newRouteQuery.set("geoname", geoName)
        }
      }
    }

    // check if we need to update the route
    if (
      currentRoutePath !== newRoutePath ||
      !isEqual(
        Array.from(currentRouteQuery.entries()).sort(),
        Array.from(newRouteQuery.entries()).sort()
      )
    ) {
      const queryString = newRouteQuery.toString()
      const newUrl = queryString
        ? `${newRoutePath}?${queryString}`
        : newRoutePath

      // For query-only changes (e.g., adding ?ref=xyz when opening an entity sheet),
      // we use the native history API instead of Next.js router.push/replace.
      // This avoids triggering re-renders in all components that use useRouter(),
      // which would otherwise cause the entire app to re-render from the top.
      // We preserve Next.js history state (as, url) for compatibility.
      const isQueryOnlyChange =
        shallow &&
        currentRoutePath === newRoutePath &&
        typeof window !== "undefined"

      if (isQueryOnlyChange) {
        const leavingUrlPage =
          !!prev?.detailSheetLocator &&
          !currentRouteAffectingState.detailSheetLocator

        if (leavingUrlPage) {
          const unwindSteps = getDetailSheetHistoryDepth(window.history.state)
          if (unwindSteps > 0) {
            entitySheetCollapseRef.current = { targetUrl: newUrl }
            window.history.go(-unwindSteps)
            return
          }
        }

        const entityOp = replaceRoute
          ? null
          : detailSheetQueryHistoryOp(currentRouteQuery, newRouteQuery)
        const currentDetailSheetDepth = getDetailSheetHistoryDepth(
          window.history.state
        )
        const nextDetailSheetDepth =
          entityOp === "push"
            ? currentDetailSheetDepth + 1
            : currentDetailSheetDepth

        let useReplace = replaceRoute
        if (entityOp !== null) {
          if (entityOp === "replace") {
            useReplace = true
          } else if (entityOp === "push") {
            useReplace = false
          }
        }

        if (useReplace) {
          window.history.replaceState(
            buildHistoryStateWithDetailSheetDepth(
              window.history.state,
              newUrl,
              nextDetailSheetDepth
            ),
            "",
            newUrl
          )
        } else {
          window.history.pushState(
            buildHistoryStateWithDetailSheetDepth(
              window.history.state,
              newUrl,
              nextDetailSheetDepth
            ),
            "",
            newUrl
          )
        }
        prevUrl.current = newUrl
        // Note: do NOT set routeChangeInProgress here. The pushState/replaceState
        // is synchronous and prevUrl.current is already updated, which is sufficient
        // to prevent the main effect from re-processing this URL change. Setting
        // routeChangeInProgress would block subsequent Redux state changes (e.g.
        // closing the entity sheet) from updating the URL, since it's only cleared
        // inside handleRouteChangeComplete which never runs for query-only changes.
      } else if (replaceRoute) {
        void router.replace(
          {
            pathname: newRoutePath,
            query: newRouteQuery.toString(),
          },
          undefined,
          { shallow: shallow }
        )
      } else {
        void router.push(
          {
            pathname: newRoutePath,
            query: newRouteQuery.toString(),
          },
          undefined,
          { shallow: shallow }
        )
      }

      if (
        entityIsPlace(currEntity) &&
        prev?.entitySheetEntity?.entity_ref !== currEntity.entity_ref
      ) {
        void (async () => {
          const placeDetailCta = placeDetailCTA(currEntity, bookDirectOnly)
          sendAnalyticsEvent({
            name: "ViewPlaceDetailSlideOut",
            properties: {
              ...placeToAmplitudeProperties(currEntity),
              urlPath: newRoutePath,
              ctaButtonLabel: placeDetailCta.ctaButtonLabel || "none",
              context: entitySheetTrackingContext,
            },
          })
        })()
      } else if (
        entityIsGuide(currEntity) &&
        prev?.entitySheetEntity?.entity_ref !== currEntity.entity_ref
      ) {
        void (async () => {
          sendAnalyticsEvent({
            name: "ViewGuideDetailSlideOut",
            properties: {
              inspiration: inspirationToAmplitudeProperties(currEntity),
              context: entitySheetTrackingContext,
            },
          })
        })()
      }

      if (
        entityIsEventActor(currEntity) &&
        prev?.entitySheetEntity?.entity_ref !== currEntity.entity_ref
      ) {
        sendAnalyticsEvent({
          name: "ViewEventActorDetailSlideOut",
          properties: {
            event_actor: eventActorToAmplitudeProperties(currEntity),
            urlPath: newRoutePath,
            context: entitySheetTrackingContext,
          },
        })
      }

      if (
        currentRouteAffectingState.tripInSheetStack &&
        !prev?.tripInSheetStack
      ) {
        sendAnalyticsEvent("ViewTripSlideOut")
      }
    }
  }

  async function handleRouteChangeComplete(url: string) {
    const queryParams = new URLSearchParams(url.split("?")[1] ?? "")
    const entityRef = entitySheetEntityRef
    const currentPriceQuoteRef =
      singleParam(queryParams.get("quoteRef")) || undefined

    // navigate to entity in history if it exists
    const navigateToLocatorInHistory = (
      locator: NonNullable<ReturnType<typeof locatorFromQuery>>
    ) => {
      const index = entitySheetHistory.stack.findIndex((entry) =>
        entryMatchesLocator(entry, locator)
      )
      if (index !== -1) {
        void dispatch(navigateToHistoryIndexThunk(index))
      }
    }

    // Show a URL locator's page: leave it if already current, step to it in
    // history if it's there, otherwise open it fresh
    const syncLocatorPage = (
      locator: NonNullable<ReturnType<typeof locatorFromQuery>>,
      isCurrent: boolean,
      open: () => void
    ) => {
      if (isCurrent) return
      const inHistory = entitySheetHistory.stack.some((entry) =>
        entryMatchesLocator(entry, locator)
      )
      if (inHistory) {
        navigateToLocatorInHistory(locator)
      } else {
        open()
      }
    }

    const urlLocator = locatorFromQuery(queryParams)
    const urlRefKind = urlLocator ? sheetKindForRef(urlLocator.ref) : undefined

    try {
      const previousUrl = prevUrl.current

      // If we transition out of chat page, we should clear the chat properties
      if (previousUrl && isChatPageUrl(previousUrl) && !isChatPageUrl(url)) {
        clearAnalyticsChatProperties()
        dispatch(userSlice.actions.resetChat())
      }

      prevUrl.current = url
      routeChangeInProgress.current = url

      // flight search params
      if (isFlightSearchPageUrl(url)) {
        const expired = queryParams.get("expired") === "1"
        if (expired) {
          dispatch(flightSearchSlice.actions.setExpiredPricesBanner(true))
          queryParams.delete("expired")
          const pathname = url.split("?")[0]
          const queryString = queryParams.toString()
          const cleanUrl = queryString ? `${pathname}?${queryString}` : pathname
          window.history.replaceState(
            { ...window.history.state, as: cleanUrl, url: cleanUrl },
            "",
            cleanUrl
          )
        }

        const updatedSearchInput = buildFlightSearchInputFromParams(queryParams)

        // if we arrived here via browser back from a flight booking page, treat
        // this as a "revise" navigation so initializeFlightSheetRequest will
        // pull the cached contract itinerary (written by the booking page) and
        // skip a full departure_search refetch that otherwise leaves the sheet
        // stuck on the refresh loader
        if (previousUrl && isFlightBookingPageUrl(previousUrl)) {
          updatedSearchInput.revise = true
        }

        void dispatch(
          openFlightSheetWithSearchInput({ searchInput: updatedSearchInput })
        )
      } else {
        void dispatch(closeFlightSheet())
      }

      let currPlaceRef: PlaceRef | undefined = undefined
      let currInspirationRef: InspirationRef | undefined = undefined
      let currEventActorRef: EventActorRef | undefined = undefined

      if (urlLocator && isAgentRunRef(urlLocator.ref)) {
        const runRef = urlLocator.ref
        syncLocatorPage(
          urlLocator,
          detailSheetCurrentPage?.kind === "agent_run_report" &&
            detailSheetCurrentPage.runRef === runRef,
          () => void dispatch(openAgentRunReportSheet({ runRef }))
        )
      } else if (urlRefKind === "action_pill_answer" && urlLocator) {
        const pillRef = urlLocator.ref
        syncLocatorPage(
          urlLocator,
          detailSheetCurrentPage?.kind === "action_pill_answer" &&
            detailSheetCurrentPage.pillRef === pillRef,
          () => void dispatch(openActionPillAnswerSheet({ pillRef }))
        )
      } else if (urlRefKind === "web_event" && urlLocator) {
        const ref = urlLocator.ref
        syncLocatorPage(
          urlLocator,
          detailSheetCurrentPage?.kind === "search_result" &&
            detailSheetCurrentPage.searchResult.ref === ref,
          () => void dispatch(openWebEventSheetByRef({ ref }))
        )
      } else if (urlLocator && isTripRef(urlLocator.ref)) {
        const tripRef = urlLocator.ref
        const urlTripTab = queryParams.get("tripTab")
        const tab = isTripTab(urlTripTab) ? urlTripTab : undefined
        const isCurrent =
          detailSheetCurrentPage?.kind === "trip" &&
          detailSheetCurrentPage.tripRef === tripRef
        syncLocatorPage(
          urlLocator,
          isCurrent,
          () => void dispatch(openTripSheet({ tripRef, tab }))
        )
        if (isCurrent && tab && tab !== selectedTripTab) {
          dispatch(tripSlice.actions.setCurrentTab(tab))
        }
      }

      if (!urlRefKind || urlRefKind === "entity") {
        // Deal with url with ...?ref=<entity_ref>
        const currentPlaceParams = getPagePlaceParams(url)
        currPlaceRef = currentPlaceParams.placeRef
        if (currPlaceRef) {
          const isSamePlaceOpen = currPlaceRef === entityRef
          const hasQuoteContextChanged =
            isSamePlaceOpen && entitySheetPriceQuoteRef !== currentPriceQuoteRef

          if (hasQuoteContextChanged) {
            void dispatch(
              openEntitySheet({
                entityRef: currPlaceRef,
                priceQuoteRef: currentPriceQuoteRef,
                entitySheetTrackingContext: "ClientRouteHandler",
              })
            )
          } else if (currPlaceRef !== entityRef) {
            const historyEntry = entitySheetHistory.stack.find(
              (entry) =>
                entry.kind === "entity" && entry.entityRef === currPlaceRef
            )

            if (historyEntry && !currentPriceQuoteRef) {
              navigateToLocatorInHistory({ ref: currPlaceRef })
            } else {
              void dispatch(
                openEntitySheet({
                  entityRef: currPlaceRef,
                  priceQuoteRef: currentPriceQuoteRef,
                  entitySheetTrackingContext: "ClientRouteHandler",
                })
              )
            }
          }
        } else {
          const currentInspirationParams = getPageInspirationParams(url)
          // Inspiration and Admin page has full page of inspiration. So we no longer need to open the entity sheet.
          // This prevent the double fetching of inspiration which is unnecessary.
          if (!(isAdminPageUrl(url) || isInspirationPageUrl(url))) {
            currInspirationRef = currentInspirationParams.inspirationRef

            if (
              currInspirationRef &&
              entitySheetEntity?.entity_ref !== currInspirationRef
            ) {
              const historyEntry = entitySheetHistory.stack.find(
                (entry) =>
                  entry.kind === "entity" &&
                  entry.entityRef === currInspirationRef
              )

              if (historyEntry) {
                navigateToLocatorInHistory({ ref: currInspirationRef })
              } else {
                try {
                  const newInspiration = await dispatch(
                    inspirationApi.endpoints.getDetailedInspiration.initiate({
                      inspirationRef: currInspirationRef,
                      batchSize: DefaultInspirationItemFetchSize,
                    })
                  ).unwrap()
                  if (newInspiration) {
                    void dispatch(
                      openEntitySheet({
                        entity: newInspiration,
                        entitySheetTrackingContext: "ClientRouteHandler",
                      })
                    )
                  }
                } catch (e) {
                  if (!httpIsCancelled(e)) {
                    toastManager.add({
                      type: "error",
                      description: "Failed to load guide",
                    })
                    console.log("Failed to load guide", e)
                  }
                }
              }
            }
          }
        }

        if (!isEventActorPageUrl(url)) {
          const currentEventActorParams = getPageEventActorParams(url)
          currEventActorRef = currentEventActorParams.eventActorRef

          if (
            currEventActorRef &&
            entitySheetEntity?.entity_ref !== currEventActorRef
          ) {
            const historyEntry = entitySheetHistory.stack.find(
              (entry) =>
                entry.kind === "entity" && entry.entityRef === currEventActorRef
            )

            if (historyEntry) {
              navigateToLocatorInHistory({ ref: currEventActorRef })
            } else {
              try {
                const newEventActor = await dispatch(
                  eventActorApi.endpoints.getDetailedEventActor.initiate({
                    eventActorRef: currEventActorRef,
                  })
                ).unwrap()
                if (newEventActor) {
                  void dispatch(
                    openEntitySheet({
                      entity: newEventActor,
                      entitySheetTrackingContext: "ClientRouteHandler",
                    })
                  )
                }
              } catch (e) {
                if (!httpIsCancelled(e)) {
                  toastManager.add({
                    type: "error",
                    description: "Failed to load event actor",
                  })
                  console.log("Failed to load event actor", e)
                }
              }
            }
          }
        }
      }

      const currentPage = detailSheetCurrentPage
      const currentPageHasUrl = currentPage
        ? locatorForEntry(currentPage) !== null
        : false

      // If no URL locator, close when the current page is URL-capable.
      if (
        !urlLocator &&
        !currPlaceRef &&
        !currInspirationRef &&
        !currEventActorRef &&
        entitySheetOpen &&
        currentPageHasUrl
      ) {
        const isTransitioningFromPlaceToGuide =
          entitySheetEntity && entityIsGuide(entitySheetEntity)

        if (!isTransitioningFromPlaceToGuide) {
          void dispatch(closeDetailSheet())
        }
      }

      // Legacy deep links (emails, push notifications): ?tripSheet=1&tripTab=
      const { isOpen: urlTripSheetOpen, tab: currentTripTab } =
        getPageIsTripSheetOpenParams(queryParams)

      if (urlTripSheetOpen) {
        if (!tripPageActive) {
          void dispatch(
            openTripSheet({ tab: currentTripTab, tripRef: tripRefFromUrl(url) })
          )
        } else if (currentTripTab && currentTripTab !== selectedTripTab) {
          dispatch(tripSlice.actions.setCurrentTab(currentTripTab))
        }
      } else if (!tripInSheetStack) {
        dispatch(tripSlice.actions.clearHighlightedEvent())
      }

      if (isSavedPageUrl(url)) {
        const querySavedTab = singleParam(queryParams.get("tab"))
        const querySavedType = singleParam(queryParams.get("type"))

        if (
          querySavedTab &&
          SAVED_TAB_VALUES.includes(querySavedTab as SavedTab)
        ) {
          dispatch(savedSlice.actions.setCurrentTab(querySavedTab as SavedTab))
        }

        if (
          querySavedType &&
          SAVED_TYPE_VALUES.includes(querySavedType as SavedType)
        ) {
          dispatch(
            savedSlice.actions.setSelectedType(querySavedType as SavedType)
          )
        }
      }

      if (isInspirationPageUrl(url)) {
        const queryInspirationPage = singleParam(queryParams.get("page"))
        const queryInspirationSearch = singleParam(queryParams.get("search"))

        const { inspirationRef } = getPageInspirationParams(url)
        if (!inspirationRef) {
          dispatch(
            inspirationSlice.actions.setSearchTerm(queryInspirationSearch ?? "")
          )
          if (isCrawler) {
            const pageNum = queryInspirationPage
              ? parseInt(queryInspirationPage)
              : 1
            dispatch(
              inspirationSlice.actions.setPaging({
                currentPage: pageNum,
                maxPage: inspirationMaxPage,
              })
            )
          }
        }
      }

      if (isExplorePageUrl(url)) {
        const queryExploreType =
          singleParam(queryParams.get("type")) || EXPLORE_TYPE_ALL
        const searchParam = singleParam(queryParams.get("search"))
        const locParam = singleParam(queryParams.get("loc"))

        if (locParam) {
          try {
            const location = await dispatch(
              placeApi.endpoints.getPlace.initiate({
                placeRef: locParam as PlaceRef,
              })
            ).unwrap()
            if (location) {
              dispatch(
                exploreSlice.actions.setLocation({
                  location: location as ExploreLocation,
                  isUserSelected: true,
                })
              )
            }
          } catch (e) {
            if (!httpIsCancelled(e)) {
              console.error("Failed to load location", e)
            }
          }
        }

        if (EXPLORE_TYPES.includes(queryExploreType as ExploreType)) {
          dispatch(
            exploreSlice.actions.setSelectedType(
              queryExploreType as ExploreType
            )
          )
        }
        if (searchParam) {
          dispatch(exploreSlice.actions.setSearchString(searchParam))
        }
      }

      if (isCreatePageUrl(url)) {
        const queryCreateTab =
          singleParam(queryParams.get("tab")) || CreateTabGuidesType
        if (
          queryCreateTab &&
          CreateTabTypeList.includes(queryCreateTab as CreateTabType)
        ) {
          dispatch(
            creatorSlice.actions.setCreateTab(queryCreateTab as CreateTabType)
          )
        }
      }

      if (isAdminPageUrl(url)) {
        const defaultTab = canAdminInspiration
          ? AdminTabDraftsType
          : canAdminVanityLink || canViewAllVanityReport
            ? AdminTabReportType
            : undefined
        const queryAdminTab = singleParam(queryParams.get("tab")) || defaultTab
        if (
          queryAdminTab &&
          AdminTabTypeList.includes(queryAdminTab as AdminTabType)
        ) {
          dispatch(
            creatorSlice.actions.setAdminTab(queryAdminTab as AdminTabType)
          )
        }
      }

      if (isProfilePageUrl(url)) {
        if (initUser && isOwnProfilePageUrl(url, initUser.handle)) {
          const queryProfileTab =
            singleParam(queryParams.get("tab")) || ProfileTabDefault
          if (
            queryProfileTab &&
            ProfileTabTypeValues.includes(queryProfileTab as ProfileTabType)
          ) {
            dispatch(
              profileSlice.actions.setUserProfileTab(
                queryProfileTab as ProfileTabType
              )
            )
          }
        } else {
          const queryPublicProfileTab =
            singleParam(queryParams.get("tab")) || PublicProfileDefaultTab
          if (
            queryPublicProfileTab &&
            PublicProfileTabTypeValues.includes(
              queryPublicProfileTab as PublicProfileTabType
            )
          ) {
            dispatch(
              publicProfileSlice.actions.setPublicTab(
                queryPublicProfileTab as PublicProfileTabType
              )
            )
          }
        }
      }

      if (isSettingPageUrl(url)) {
        const hasEaParam = queryParams.has("ea")
        const querySettingsTab = hasEaParam
          ? SettingEarlyAccessTab
          : singleParam(queryParams.get("section"))
        if (!querySettingsTab) {
          void dispatch(changeSettingsTab({ tab: null }))
        } else if (
          SettingTabTypeValues.includes(querySettingsTab as SettingTabType)
        ) {
          void dispatch(
            changeSettingsTab({ tab: querySettingsTab as SettingTabType })
          )
        }
      }

      // Verify email open
      const { isVerify: isVerifyEmailOpen } =
        getPageIsVerifyEmailOpenParams(router)

      if (isVerifyEmailOpen) {
        dispatch(userSlice.actions.setEmailVerifyMode(true))

        // Once we set the Redux, remove the param verfiy=1 from the URL
        const { pathname, query } = router
        delete query["verify"]
        void router.replace(
          {
            pathname: pathname,
            query: query,
          },
          undefined,
          { shallow: true }
        )
      }

      // Local timezone (experimental feature)
      const isLocalTime = queryParams.get("localTime") === "1"
      if (isLocalTime) {
        dispatch(itinerarySlice.actions.setCalendarLocalTimeEnable(true))
      }

      // capture if currently handling a route change
      if (routeChangeInProgress.current === url) {
        routeChangeInProgress.current = null
      }
    } catch (e) {
      // ignore cancel errors
      if (!httpIsCancelled(e)) {
        void dispatch(closeDetailSheet())
        throw e
      }
    }
  }

  // Keep ref updated with the latest handleRouteChangeComplete so popstate handler
  // always calls the current version without needing it in the effect's dependencies
  handleRouteChangeCompleteRef.current = handleRouteChangeComplete
  entitySheetOpenRef.current = entitySheetOpen
  entitySheetHistoryRef.current = entitySheetHistory

  useEffect(() => {
    if (!isClientSide()) return

    // Prevent Next.js from doing a full (non-shallow) route change for
    // query-only popstate events. These occur when the user clicks
    // back/forward between history entries that were pushed via
    // window.history.pushState (used for query-only URL changes to avoid
    // re-renders). Without this, Next.js would trigger getServerSideProps
    // and cause the page to show a loading state and fully reinitialize.
    router.beforePopState(({ as: targetAs }) => {
      const targetPath = (targetAs || "").split("?")[0]
      const routerPath = router.asPath.split("?")[0]

      if (targetPath === routerPath) {
        // Query-only change — our popstate listener handles the Redux sync.
        // Tell Next.js not to do a full navigation.
        return false
      }

      // Path changed — let Next.js handle it normally.
      return true
    })

    const handlePopState = () => {
      const collapse = entitySheetCollapseRef.current
      if (collapse) {
        entitySheetCollapseRef.current = null
        const targetUrl = collapse.targetUrl
        window.history.replaceState(
          buildHistoryStateWithDetailSheetDepth(
            window.history.state,
            targetUrl,
            0
          ),
          "",
          targetUrl
        )
        prevUrl.current = targetUrl
        void handleRouteChangeCompleteRef.current?.(targetUrl)
        return
      }
      const targetUrl = `${mtLocation.pathname}${mtLocation.search}`
      const targetQuery = new URLSearchParams(mtLocation.search)
      const targetLocator = locatorFromQuery(targetQuery)

      const historyIndex = getPopstateDetailSheetHistoryIndex(
        entitySheetOpenRef.current,
        entitySheetHistoryRef.current,
        targetLocator
      )
      if (historyIndex !== null) {
        prevUrl.current = targetUrl
        void dispatch(navigateToHistoryIndexThunk(historyIndex))
        return
      }

      void handleRouteChangeCompleteRef.current?.(targetUrl)
    }

    window.addEventListener("popstate", handlePopState)
    return () => {
      router.beforePopState(() => true)
      window.removeEventListener("popstate", handlePopState)
    }
  }, [router, dispatch])

  // this needs to be in a useEffect because we don't want to interfere with rendering components
  // I was getting a bunch of "Cannot update a component X while rendering a different component Y" errors when
  // not using useEffect, also note there is no dependency array here, this is intentional as we want this to run
  // on every render
  useEffect(() => {
    // this should all be done client side
    if (isClientSide()) {
      // important check, as could be in progress or cancelled if not ready
      if (router.isReady) {
        // current redux state that affects the route
        const currentRouteAffectingState: RouteAffectingState = {
          tripInSheetStack,
          chatId,
          flightSearchInput,
          detailSheetLocator,
          entitySheetEntity,
          entitySheetItineraryEvent,
          entitySheetPriceQuoteRef,
          emailVerifyMode,
          profileSetupDialogOpen,
          selectedSavedTab,
          selectedSavedType,
          selectedCreateTab,
          selectedInspirationSearchTerm,
          inspirationCurrentPage,
          selectedExploreType,
          selectedExploreLocation,
          selectedExploreSearchString,
          selectedAdminTab,
          selectedProfileTab,
          selectedPublicProfileTab,
          selectedSettingTab,
          collectionCurrentPage,
        }

        // route change takes precedence over redux state change, so check for that first
        const currentUrl = getCurrentUrl()
        if (prevUrl.current !== currentUrl) {
          const prevPath = prevUrl.current?.split("?")[0]
          const currentPath = currentUrl.split("?")[0]

          if (prevPath !== currentPath) {
            dispatch(savedSlice.actions.setLastUsedCollection(undefined))
            dispatch(tripSlice.actions.setLastUsedTripRef(undefined))
            dispatch(setHoveredPlace(undefined))
            dispatch(setHoveredFlight(undefined))
          }

          void handleRouteChangeComplete(currentUrl)

          // so that we don't trigger a route change on the next render
          prevRouteAffectingState.current = currentRouteAffectingState
        } else if (prevRouteAffectingState.current == null) {
          // set prevRouteAffectingState to currentRouteAffectingState this is to help track redux changes
          // prevRouteAffectingState is null on initial render
          prevRouteAffectingState.current = currentRouteAffectingState
        } else if (
          routeChangeInProgress.current == null &&
          !isEqual(prevRouteAffectingState.current, currentRouteAffectingState)
        ) {
          handleReduxStateChange(currentRouteAffectingState)
        }
      }
    }
  })

  useEffect(() => {
    if (isClientSide()) {
      if (router.isReady) {
        if (!isEmbedded()) {
          const showCookiesBannerIfNecessary = () => {
            const seenCookiesBanner =
              webStorage.getItem("seenCookiesBanner") === "true" ||
              currUser?.functional_cookies_enabled !== undefined ||
              currUser?.advertising_cookies_enabled !== undefined

            const shouldShow = !seenCookiesBanner

            if (shouldShow !== showCookiesBanner) {
              dispatch(userSlice.actions.setShowCookiesBanner(shouldShow))
            }
          }
          showCookiesBannerIfNecessary()
        }
      }
    }
  }, [
    currUser?.advertising_cookies_enabled,
    currUser?.functional_cookies_enabled,
    dispatch,
    router.isReady,
    showCookiesBanner,
  ])

  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      const currentPath = router.asPath.split("?")[0]
      const newPath = new URL(url, mtLocation.origin).pathname

      if (currentPath !== newPath) {
        dispatch(setPreviousPage(`${mtLocation.pathname}${mtLocation.search}`))
      }
    }

    router.events.on("routeChangeStart", handleRouteChangeStart)

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart)
    }
  }, [router, dispatch])

  return null
}
